import React, { useMemo } from 'react';

import { ActivityLogEvent } from '@/constants/activityLogEvent';
import AchievementEvent from '@/pages/Timeline/components/AchievementEvent';
import AppointmentAttendanceEvent from '@/pages/Timeline/components/AppointmentAttendanceEvent';
import DefaultTimelineSubcomponent from '@/pages/Timeline/components/DefaultTimelineSubcomponent';
import DrugScreenEvent from '@/pages/Timeline/components/DrugScreenEvent';
import EpisodeOfCareEvent from '@/pages/Timeline/components/EpisodeOfCareEvent';
import MessageEvent from '@/pages/Timeline/components/MessageEvent/MessageEvent';
import MissionEvent from '@/pages/Timeline/components/MissionEvent';
import NoteEvent from '@/pages/Timeline/components/NoteEvent/NoteEvent';
import PhaseEvent from '@/pages/Timeline/components/PhaseEvent/PhaseEvent';
import ProgramQualificationEvent from '@/pages/Timeline/components/ProgramQualificationEvent/ProgramQualificationEvent';
import ReferralCreatedEvent from '@/pages/Timeline/components/ReferralCreatedEvent';
import SurveyEvent from '@/pages/Timeline/components/SurveyEvent/SurveyEvent';
import TaskEvent from '@/pages/Timeline/components/TaskEvent/TaskEvent';
import UserChallengeEvent from '@/pages/Timeline/components/UserChallengeEvent';
import type { ActivityLogWithParsedData } from '@/pages/Timeline/types';
import parseActivityLogData from '@/utils/parseActivityLogData';

import { Props } from './types';

// [NOTE]: logic for this component is tested in Timeline.test.tsx
const ActivityLog = ({ activityLog }: Props) => {
  const { action: event } = activityLog;

  const activityLogWithParsedData: ActivityLogWithParsedData = useMemo(() => {
    return {
      ...activityLog,
      data: parseActivityLogData(activityLog.data)
    };
  }, [activityLog]);

  switch (true) {
    case event === ActivityLogEvent.person_achievement_created:
      return <AchievementEvent activityLog={activityLogWithParsedData} />;
    case event === ActivityLogEvent.appointment_canceled:
    case event === ActivityLogEvent.appointment_confirmed:
    case event === ActivityLogEvent.appointment_scheduled:
    case event === ActivityLogEvent.appointment_status:
    case event === ActivityLogEvent.APPOINTMENT_CANCELED_EVENT:
    case event === ActivityLogEvent.APPOINTMENT_CONFIRMED_EVENT:
    case event === ActivityLogEvent.APPOINTMENT_SCHEDULED_EVENT:
    case event === ActivityLogEvent.APPOINTMENT_STATUS_EVENT:
      return <AppointmentAttendanceEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.CHALLENGE_FINISHED_EVENT:
    case event === ActivityLogEvent.challenge_finished:
      return <UserChallengeEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.drug_screen_occured:
    case event === ActivityLogEvent.DRUG_SCREEN_OCCURRED_EVENT:
      return <DrugScreenEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.episode_of_care_admitted:
    case event === ActivityLogEvent.episode_of_care_discharged:
    case event === ActivityLogEvent.EPISODE_OF_CARE_ADMITTED:
    case event === ActivityLogEvent.EPISODE_OF_CARE_DISCHARGED:
      return <EpisodeOfCareEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.inbound_message_created:
    case event === ActivityLogEvent.INBOUND_MESSAGE_CREATED_EVENT:
    case event === ActivityLogEvent.outbound_message_created:
    case event === ActivityLogEvent.OUTBOUND_MESSAGE_CREATED_EVENT:
      return <MessageEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.mission_finished:
    case event === ActivityLogEvent.MISSION_FINISHED_EVENT:
      return <MissionEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.kustomer_note_created:
    case event?.includes('note_created'):
      return <NoteEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.phase_transition:
    case event === ActivityLogEvent.PHASE_TRANSITION_EVENT:
      return <PhaseEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.program_qualification:
    case event === ActivityLogEvent.program_qualification_disqualified:
    case event === ActivityLogEvent.program_qualification_intake_rejected:
    case event === ActivityLogEvent.PROGRAM_QUALIFICATION_EVENT:
    case event === ActivityLogEvent.PROGRAM_QUALIFICATION_DISQUALIFIED_EVENT:
    case event === ActivityLogEvent.PROGRAM_QUALIFICATION_INTAKE_REJECTED_EVENT:
      return <ProgramQualificationEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.referral_created:
    case event === ActivityLogEvent.REFERRAL_CREATED_EVENT:
      return <ReferralCreatedEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.survey_submitted:
    case event === ActivityLogEvent.SURVEY_SUBMITTED_EVENT:
      return <SurveyEvent activityLog={activityLogWithParsedData} />;

    case event === ActivityLogEvent.SURVEY_PARTIAL_COMPLETION_EVENT:
    case event === ActivityLogEvent.survey_partial_completion:
      return (
        <SurveyEvent activityLog={activityLogWithParsedData} title="Survey Partial Completion" />
      );

    case event === ActivityLogEvent.work_item_created:
      return <TaskEvent activityLog={activityLogWithParsedData} />;

    default:
      return <DefaultTimelineSubcomponent activityLog={activityLogWithParsedData} />;
  }
};
export default React.memo(ActivityLog);
